/**
 * מאזן — "הדוחות שלי": drafts to continue, paid reports to download again.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Download, FilePlus2, Pencil, Trash2 } from 'lucide-react'
import { handleApiError } from '../../services/api'
import { ils, mazanApi, STATUS_LABEL, syncMazanAccess, type ReportSummary } from '../../features/mazan/model'

const dateHe = (d?: string) => (d ? new Date(d).toLocaleDateString('he-IL') : '—')

export default function MazanMyReportsPage() {
  const navigate = useNavigate()
  const [reports, setReports] = useState<ReportSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [status, setStatus] = useState('')

  const load = () =>
    mazanApi
      .list()
      .then((r) => {
        setReports(r.reports)
        syncMazanAccess()
      })
      .catch((e) => setError(handleApiError(e)))

  useEffect(() => { load() }, [])

  const create = async () => {
    setBusy('new')
    setError(null)
    try {
      const { id } = await mazanApi.create()
      navigate(`/Mazan/report/${id}`)
    } catch (e) {
      setError(handleApiError(e))
      setBusy(null)
    }
  }

  const remove = async (id: string) => {
    if (!window.confirm('למחוק את הטיוטה? לא ניתן לשחזר.')) return
    setBusy(id)
    try {
      await mazanApi.remove(id)
      setStatus('הטיוטה נמחקה')
      await load()
    } catch (e) {
      setError(handleApiError(e))
    } finally {
      setBusy(null)
    }
  }

  const download = async (id: string) => {
    setBusy(id)
    try {
      await mazanApi.downloadPdf(id)
    } catch (e) {
      setError(handleApiError(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-bold text-gray-900">איזון — הדוחות שלי</h1>
        <button type="button" className="btn btn-primary inline-flex items-center gap-2" onClick={create} disabled={busy === 'new'}>
          <FilePlus2 size={18} aria-hidden="true" /> {busy === 'new' ? 'פותח…' : 'תחשיב חדש'}
        </button>
      </div>
      <p className="mt-1 text-sm text-gray-700">תחשיב איזון משאבים לפנסיה מקיפה ומשלימה. טיוטות נשמרות אוטומטית.</p>

      <p role="status" aria-live="polite" className="sr-only">{status}</p>
      {error && <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}

      {reports === null ? (
        <p role="status" className="mt-6 text-gray-700">טוען…</p>
      ) : reports.length === 0 ? (
        <div className="card mt-6 text-center">
          <p className="text-gray-800">עדיין אין דוחות. אפשר להתחיל תחשיב חדש — התשלום רק בסוף, ומיד אחריו מוצגות התוצאות והדוח המלא.</p>
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {reports.map((r) => (
            <li key={r.id} className="card flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-gray-900">{STATUS_LABEL[r.status]}</p>
                <p className="text-sm text-gray-700">
                  {r.status === 'draft' ? `עודכן ${dateHe(r.updatedAt)}` : `שולם ${dateHe(r.paidAt)}`}
                  {r.headline?.valid && r.status !== 'draft' && r.status !== 'pending_payment' ? ` · נטו לתשלום ${ils.format(r.headline.totalNet)}` : ''}
                  {r.priceIls != null && r.status !== 'draft' ? ` · ${ils.format(r.priceIls)}` : ''}
                </p>
              </div>
              <div className="flex gap-2">
                {r.status === 'draft' && (
                  <>
                    <Link to={`/Mazan/report/${r.id}`} className="btn btn-secondary inline-flex items-center gap-1">
                      <Pencil size={16} aria-hidden="true" /> המשך
                    </Link>
                    <button type="button" className="btn btn-outline inline-flex items-center gap-1" onClick={() => remove(r.id)} disabled={busy === r.id}
                      aria-label={`מחיקת הטיוטה מ־${dateHe(r.updatedAt)}`}>
                      <Trash2 size={16} aria-hidden="true" /> מחיקה
                    </button>
                  </>
                )}
                {r.status === 'generated' && (
                  <button type="button" className="btn btn-primary inline-flex items-center gap-1" onClick={() => download(r.id)} disabled={busy === r.id}>
                    <Download size={16} aria-hidden="true" /> {busy === r.id ? 'מוריד…' : 'הורדת הדוח'}
                  </button>
                )}
                {(r.status === 'pending_payment' || r.status === 'paid' || r.status === 'failed') && (
                  <Link to={`/Mazan/result/${r.id}`} className="btn btn-secondary">מצב הדוח</Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
